import { useEffect, useMemo, useState } from 'react';
import { NotFound, PageSection } from '@src/components';
import { useLazyGetCountriesQuery } from '@src/services/country/countryApiSlice';
import { useLazyGetRegionsQuery } from '@src/services/region/regionApiSlice';
import { SearchInput } from './SearchInput';
import { SelectedFilters } from './SelectedFilters';
import { CountryList } from '../../Country';
import { StoreTab } from '../Store';
import { RegionFilter } from './RegionFilter';
import { Sort } from './Sort';
import { DataTableSkeleton } from '@src/components/Loading/DataTableSkeleton';
import { GlobalIcon, GlobalRefreshIcon } from '@src/components/svg';
import { Button } from 'antd';
interface CountryViewProps {
  onTabChange: (value: StoreTab) => void;
}

export function CountryView({ onTabChange }: Readonly<CountryViewProps>) {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedSort, setSelectedSort] = useState<{
    type: string;
    direction: 'asc' | 'desc' | null;
  }>({ type: '', direction: null });

  const [selectedRegionIds, setSelectedRegionIds] = useState<string[]>([]);

  const [
    getCountries,
    {
      data: countries,
      isLoading: isCountriesLoading,
      isFetching: isCountriesFetching,
    },
  ] = useLazyGetCountriesQuery();

  const [
    getRegions,
    {
      data: regions,
      isLoading: isRegionsLoading,
      isFetching: isRegionsFetching,
    },
  ] = useLazyGetRegionsQuery();

  useEffect(() => {
    getCountries({ includeRegion: true });
    getRegions({ includeCountries: true });
  }, []);

  const filteredAndSortedCountries = useMemo(() => {
    if (!countries) return [];

    let result = [...countries];

    if (selectedRegionIds.length > 0) {
      result = result.filter((country) =>
        selectedRegionIds.includes(country.region?.id)
      );
    }

    if (searchQuery.trim()) {
      const lowerQuery = searchQuery.toLowerCase();
      result = result.filter((country) =>
        country.name.toLowerCase().includes(lowerQuery)
      );
    }

    const { direction, type } = selectedSort;

    if (direction && type) {
      result.sort((a, b) => {
        let aValue: string | number = '';
        let bValue: string | number = '';

        switch (type) {
          case 'alphabetical':
            aValue = a.name;
            bValue = b.name;
            break;
          case 'popular':
            aValue = a.clickCount || 0;
            bValue = b.clickCount || 0;
            break;
          case 'region_based':
            aValue = a.region?.name || '';
            bValue = b.region?.name || '';
            break;
          default:
            return 0;
        }

        if (typeof aValue === 'string' && typeof bValue === 'string') {
          return direction === 'asc'
            ? aValue.localeCompare(bValue)
            : bValue.localeCompare(aValue);
        } else if (typeof aValue === 'number' && typeof bValue === 'number') {
          return direction === 'asc' ? aValue - bValue : bValue - aValue;
        }

        return 0;
      });
    } else {
      result.sort((a, b) => a.name.localeCompare(b.name));
    }

    return result;
  }, [countries, selectedRegionIds, selectedSort, searchQuery]);

  if (
    isCountriesLoading ||
    isCountriesFetching ||
    isRegionsLoading ||
    isRegionsFetching
  ) {
    return <DataTableSkeleton />;
  }

  return (
    <>
      <PageSection className='custom-scrollbar bg-wl-background py-4 px-6 mb-4'>
        <div className='flex flex-col md:flex-row md:justify-between md:items-center gap-2'>
          <div className='flex items-center gap-2 w-full md:w-auto order-1 sm:order-2'>
            <div className='w-full md:w-64'>
              <SearchInput value={searchQuery} onChange={setSearchQuery} />
            </div>
            {regions && regions.length > 0 && (
              <RegionFilter
                onConfirm={(checkedItems) => {
                  const ids = Object.values(checkedItems)
                    .filter((item) => item.checked)
                    .map((item) => item.id);
                  setSelectedRegionIds(ids);
                }}
                prevFilters={selectedRegionIds.reduce(
                  (acc, id) => {
                    acc[id] = true;
                    return acc;
                  },
                  {} as Record<string, boolean>
                )}
                filteredCount={selectedRegionIds.length}
              />
            )}
            <Sort
              selectedSort={selectedSort}
              onConfirm={(selected: Record<string, unknown>) => {
                if (Object.keys(selected).length === 0) {
                  setSelectedSort({
                    type: '',
                    direction: null,
                  });
                } else {
                  const [type, direction] = Object.entries(selected)[0];
                  const validDirection =
                    direction === 'asc' || direction === 'desc'
                      ? direction
                      : null;
                  setSelectedSort({
                    type,
                    direction: validDirection,
                  });
                }
              }}
            />
          </div>

          <div className='flex gap-2 order-2 sm:order-1'>
            <Button
              size='large'
              className='w-1/2 md:w-fit rounded-xl !bg-wl-surface !border-none'
            >
              <span className='flex gap-2 text-wl-primary'>
                <GlobalRefreshIcon />
                <span className='text-B-16'>Countries</span>
              </span>
            </Button>
            <Button
              variant='text'
              size='large'
              className='w-1/2 md:w-fit border-none shadow-none hover:!bg-transparent hover:!text-wl-primary text-wl-on-background bg-transparent'
              onClick={() => onTabChange('region')}
            >
              <span className='flex gap-2'>
                <GlobalIcon /> <span className='text-M-16'>Regions</span>
              </span>
            </Button>
          </div>
        </div>
      </PageSection>

      <SelectedFilters
        regions={regions || []}
        selectedRegionIds={selectedRegionIds}
        selectedSort={selectedSort}
        onClearRegion={(id) =>
          setSelectedRegionIds((prev) => prev.filter((rid) => rid !== id))
        }
        onClearAllRegions={() => setSelectedRegionIds([])}
        onClearSort={() => setSelectedSort({ type: '', direction: null })}
      />

      {filteredAndSortedCountries.length === 0 && <NotFound />}
      <CountryList countries={filteredAndSortedCountries} />
    </>
  );
}
